import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);
  const headerClasses = isScrolled ? "bg-background/95 backdrop-blur-md text-primary shadow-md py-3 border-b border-border" : "bg-primary text-primary-foreground py-5 border-b border-transparent";
  const logoSrc = isScrolled ? "/img/canada_integration_logo_dark.png" : "/img/canada_integration_logo_white.png";
  const linkColorClass = isScrolled ? "text-primary hover:text-accent" : "text-primary-foreground hover:text-accent";
  const mobileButtonClass = isScrolled ? "text-primary hover:bg-accent/10" : "text-primary-foreground hover:bg-primary-foreground/10";
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerClasses}`}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          {}
          <Link to="/HomePage" className="flex items-center gap-2 z-50 relative">
            <img src={logoSrc} alt="Logo" className="h-8 w-auto object-contain transition-all duration-300" onError={e => {
            e.currentTarget.style.display = 'none';
            const p = e.currentTarget.parentElement;
            if (p) p.innerHTML += `<span class="font-serif font-bold text-xl tracking-tight ${isScrolled ? 'text-primary' : 'text-primary-foreground'}">Bodywerks</span>`;
          }} />
          </Link>

          {}
          <div className="hidden lg:block">
            {!loading && <NavigationMenu>
                <NavigationMenuList className="gap-2">
                  {menuItems.map(item => <NavigationMenuItem key={item.id}>
                      {item.children && item.children.length > 0 ? <>
                          <NavigationMenuTrigger className={`bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent ${linkColorClass} font-medium text-sm`}>
                            {item.label}
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                              {item.children.map(child => <li key={child.id}>
                                  <NavigationMenuLink asChild>
                                    <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                                      <div className="text-sm font-medium leading-none">{child.label}</div>
                                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                                        Explore {child.label.toLowerCase()}
                                      </p>
                                    </Link>
                                  </NavigationMenuLink>
                                </li>)}
                            </ul>
                          </NavigationMenuContent>
                        </> : <NavigationMenuLink asChild>
                          <Link to={item.href || "#"} className={`group inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-accent/10 focus:bg-accent/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${linkColorClass}`}>
                            {item.label}
                          </Link>
                        </NavigationMenuLink>}
                    </NavigationMenuItem>)}
                </NavigationMenuList>
              </NavigationMenu>}
          </div>

          {}
          <Button variant="ghost" size="icon" className={`lg:hidden z-50 relative ${mobileButtonClass}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </header>

      {}
      <div className={`fixed inset-0 z-40 bg-background/95 backdrop-blur-sm transition-all duration-300 lg:hidden flex flex-col pt-24 px-6 ${mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        <nav className="flex flex-col gap-6 h-full overflow-y-auto pb-10">
          {!loading && menuItems.map(item => <div key={item.id} className="flex flex-col gap-2">
              <Link to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>
              {item.children && item.children.length > 0 && <div className="pl-4 flex flex-col gap-3 border-l border-border/50">
                  {item.children.map(child => <Link key={child.id} to={child.href || "#"} className="text-base text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      {child.label}
                    </Link>)}
                </div>}
            </div>)}
          <div className="mt-6">
             <Link to="/ContactPage">
               <Button className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-medium py-6 rounded-lg">
                  Get Started
               </Button>
             </Link>
          </div>
        </nav>
      </div>
    </>;
}
function HeaderWrapper() {
  return <MenuProvider menu_id="43" className="header-menu-provider">
      <HeaderContent />
    </MenuProvider>;
}
export default function HeroPrincipal() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll();
  const {
    ref: btnRef,
    isVisible: btnVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <div data-section-id="15215" id="hero" className="relative min-h-screen flex flex-col bg-background text-foreground">
      {}
      <HeaderWrapper />

      {}
      <section className="relative flex-1 flex flex-col items-center justify-center overflow-hidden pt-[160px] pb-[160px]">
        {}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/canadian_skyline_dusk.png')] bg-cover bg-center bg-no-repeat" />
          {}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/40 to-primary/90 mix-blend-multiply" />
          <div className="absolute inset-0 bg-black/20" /> {}
        </div>

        <div className="container relative z-10 px-4 md:px-6 text-center max-w-5xl mx-auto mb-6">
          <h1 className="font-serif font-bold text-4xl md:text-5xl lg:text-7xl text-primary-foreground tracking-tight leading-[1.1] drop-shadow-lg">
            Book an Appointment
          </h1>
        </div>

        {}
        <div className="container relative z-10 px-4 md:px-6 text-center max-w-5xl mx-auto">
          <div ref={textRef} className={`transition-all duration-1000 ease-out transform ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <p className="font-serif font-semibold text-2xl md:text-3xl text-primary-foreground mb-4 drop-shadow-md">
              Expert Chiropractic Care & Advanced Rehabilitation
            </p>
            
            <p className="font-sans text-lg md:text-xl lg:text-2xl text-primary-foreground/90 max-w-3xl mx-auto mb-10 leading-relaxed drop-shadow-md">
              Restore your mobility and live pain-free with personalized chiropractic treatments in Limassol and Nicosia. Your journey to optimal physical performance starts here.
            </p>
          </div>

          <div ref={btnRef} className={`transition-all duration-1000 delay-300 ease-out transform ${btnVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <Link to="https://bodywerks.pro-posture.com">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-lg px-8 py-6 h-auto rounded-lg shadow-lg hover:-translate-y-1 transition-all duration-300">FREE POSTURE SCAN</Button>
            </Link>
          </div>
        </div>

        {}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent z-10" />
      </section>
    </div>;
}